import { DatePicker as AntdDatePicker } from 'antd';
import React, { ComponentProps } from 'react';

import {
  dayjsToMoment,
  momentToDayjs,
  type DatetimeType,
} from '@common/services/datetime';

type AntdProps = ComponentProps<typeof AntdDatePicker>;

// antd's `PickerProps<Moment>` is a union (base | date | time). A plain `Omit`
// over a union collapses to only the keys common to every member, which drops
// date-variant-only props like `showToday`. Distribute the Omit across the union
// so those props survive on the wrapper's public type.
type DistributiveOmit<T, K extends keyof never> = T extends unknown
  ? Omit<T, K>
  : never;

export type DatePickerProps = DistributiveOmit<
  AntdProps,
  'value' | 'defaultValue' | 'defaultPickerValue' | 'onChange' | 'disabledDate'
> & {
  value?: DatetimeType | null;
  defaultValue?: DatetimeType | null;
  defaultPickerValue?: DatetimeType | null;
  onChange?: (date: DatetimeType | null, dateString: string) => void;
  disabledDate?: (current: DatetimeType) => boolean;
};

export function DatePicker({
  value,
  defaultValue,
  defaultPickerValue,
  onChange,
  disabledDate,
  ...rest
}: DatePickerProps) {
  return (
    <AntdDatePicker
      {...rest}
      value={dayjsToMoment(value)}
      defaultValue={dayjsToMoment(defaultValue) ?? undefined}
      defaultPickerValue={dayjsToMoment(defaultPickerValue) ?? undefined}
      onChange={
        onChange
          ? (date, dateString) => onChange(momentToDayjs(date), dateString)
          : undefined
      }
      disabledDate={
        disabledDate
          ? (current) => disabledDate(momentToDayjs(current))
          : undefined
      }
    />
  );
}
